import { useContext } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useReadTime } from '@confluence/read-time/entry-points/useReadTime.ts';

import { CatchupPersonalizationContext } from './CatchupPersonalizationContext.ts';

import type {
	useDynamicSplitButtonLastModifiedDateQuery$data,
	useDynamicSplitButtonLastModifiedDateQuery$variables,
} from './__generated__/useDynamicSplitButtonLastModifiedDateQuery.graphql.ts';
import type {
	useDynamicSplitButtonCatchupLastViewedTimeQuery$data,
	useDynamicSplitButtonCatchupLastViewedTimeQuery$variables,
	CatchupContentType,
} from './__generated__/useDynamicSplitButtonCatchupLastViewedTimeQuery.graphql.ts';
import type {
	useDynamicSplitButtonContentHistoryQuery$data,
	useDynamicSplitButtonContentHistoryQuery$variables,
} from './__generated__/useDynamicSplitButtonContentHistoryQuery.graphql.ts';

// PersonalizationContract fragment baseline. Colocated here so the catchup last-viewed-time field
// is owned by its consumer, then spread by whichever operation supplies it. Spread unmasked below
// because this operation is still read as `$data` through `useQueryInterop`.
// See confluence/openspec/changes/personalization-contract.
export const useDynamicSplitButtonCatchupLastViewedTimeFragment: GraphQLTaggedNode = graphql`
	fragment useDynamicSplitButton_catchupLastViewedTime on CatchupLastViewedTimeResponse {
		lastViewedTimeISO
	}
`;

const useDynamicSplitButtonCatchupLastViewedTimeQuery = graphql`
	query useDynamicSplitButtonCatchupLastViewedTimeQuery(
		$contentId: ID!
		$contentType: CatchupContentType!
	) {
		catchupGetLastViewedTime(contentId: $contentId, contentType: $contentType) {
			...useDynamicSplitButton_catchupLastViewedTime @relay(mask: false)
		}
	}
`;

const useDynamicSplitButtonContentHistoryQuery = graphql`
	query useDynamicSplitButtonContentHistoryQuery($contentId: ID!) {
		contentHistory(contentId: $contentId, first: 1) {
			nodes {
				collaborators {
					userAccountIds
				}
			}
		}
	}
`;

type DynamicSplitButtonReturnType = {
	showCatchupAsDefault: boolean;
	queryLoading: boolean;
	showSummarizeAsDefault?: boolean;
	queryError?: ApolloErrorInterop | undefined;
	lastViewedDate?: Date;
};

export const useDynamicSplitButton = (
	contentId: string,
	contentType: CatchupContentType,
	isRefreshRovoButton: boolean = false,
): DynamicSplitButtonReturnType => {
	const { isAdminHubAIEnabled, userId } = useSessionData();
	const pageMode = useGetPageMode();
	const personalization = useContext(CatchupPersonalizationContext);
	const catchup =
		personalization?.contentId === contentId &&
		personalization.contentType === contentType &&
		isAdminHubAIEnabled &&
		pageMode !== PageMode.EDIT
			? personalization
			: undefined;
	const isRovoButtonEnabled = useAIRovoButtonEnablement();

	const { readTime, loading: readTimeLoading } = useReadTime(
		contentId,
		!isAdminHubAIEnabled || pageMode === PageMode.EDIT,
	);

	const {
		data,
		loading: catchupLastViewedLoading,
		error: legacyQueryError,
		// eslint-disable-next-line react-hooks/rules-of-hooks
	} = useQueryInterop<
		useDynamicSplitButtonCatchupLastViewedTimeQuery$data,
		useDynamicSplitButtonCatchupLastViewedTimeQuery$variables
	>(useDynamicSplitButtonCatchupLastViewedTimeQuery, {
		variables: {
			contentId,
			contentType,
		},
		skip: !!catchup || !isAdminHubAIEnabled || pageMode === PageMode.EDIT,
	});

	const queryError = catchup ? undefined : legacyQueryError;
	const { data: lastModifiedDateData, loading: lastModifiedLoading } = useQueryInterop<
		useDynamicSplitButtonLastModifiedDateQuery$data,
		useDynamicSplitButtonLastModifiedDateQuery$variables
	>(
		graphql`
			query useDynamicSplitButtonLastModifiedDateQuery($contentId: ID!) {
				content(id: $contentId) {
					nodes {
						id
						metadata {
							lastModifiedDate
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId,
			},
			skip: !isAdminHubAIEnabled || pageMode === PageMode.EDIT,
		},
	);

	const { data: contentHistoryData, loading: contentHistoryLoading } = useQueryInterop<
		useDynamicSplitButtonContentHistoryQuery$data,
		useDynamicSplitButtonContentHistoryQuery$variables
	>(useDynamicSplitButtonContentHistoryQuery, {
		variables: {
			contentId,
		},
		skip: !isAdminHubAIEnabled || pageMode === PageMode.EDIT || !isRovoButtonEnabled,
		fetchPolicy: 'network-only',
	});

	const lastModifiedDate =
		lastModifiedDateData?.content?.nodes?.[0]?.metadata?.lastModifiedDate ?? '';
	const lastViewedTime =
		(catchup ? catchup.data : data?.catchupGetLastViewedTime)?.lastViewedTimeISO ?? '';
	// Guard against cases where lastViewedTime would otherwise cause a NaN parse result
	const cleanedLastViewedTime = lastViewedTime.replace(/\[.*?\]$/, '');
	const parsedLastViewedTime = Date.parse(cleanedLastViewedTime);
	const parsedLastModifiedDate = Date.parse(lastModifiedDate);
	const lastEditedCollaborators =
		contentHistoryData?.contentHistory?.nodes?.[0]?.collaborators?.userAccountIds ?? [];
	const isCurrentUserARecentCollaborator = lastEditedCollaborators.some(
		(collaborator?: string | null) => collaborator === userId,
	);

	const isFirstTimeVisit = Number.isNaN(parsedLastViewedTime);
	const lastViewedDate = isFirstTimeVisit ? undefined : new Date(parsedLastViewedTime);

	const queryLoading =
		(!catchup && catchupLastViewedLoading) ||
		lastModifiedLoading ||
		contentHistoryLoading ||
		readTimeLoading;

	let showCatchupAsDefault = false;
	let showSummarizeAsDefault = true;

	if ((!parsedLastViewedTime && !isFirstTimeVisit) || !parsedLastModifiedDate || queryError) {
		return {
			showCatchupAsDefault,
			queryLoading,
			queryError,
		};
	}

	if (
		isRovoButtonEnabled &&
		pageMode !== PageMode.EDIT &&
		!queryLoading &&
		isFirstTimeVisit &&
		readTime &&
		readTime >= 5 &&
		!isCurrentUserARecentCollaborator
	) {
		if (isRefreshRovoButton) {
			return {
				showCatchupAsDefault,
				showSummarizeAsDefault,
				queryLoading,
			};
		}
	}

	showCatchupAsDefault =
		pageMode !== PageMode.EDIT &&
		!queryLoading &&
		parsedLastModifiedDate > parsedLastViewedTime &&
		!isCurrentUserARecentCollaborator;
	showSummarizeAsDefault = false;

	return {
		showCatchupAsDefault,
		showSummarizeAsDefault,
		queryLoading,
		lastViewedDate,
	};
};

export const useDynamicSplitButtonCatchupLastViewedTimeFragmentReader: GraphQLTaggedNode = graphql`
	fragment useDynamicSplitButton_catchupLastViewedTimeReader on CatchupLastViewedTimeResponse
	@throwOnFieldError {
		...useDynamicSplitButton_catchupLastViewedTime @relay(mask: false)
	}
`;
